import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cases, CASE_TYPES, type CaseType } from '../../../mocks/cases';
import CaseCompare from './CaseCompare';
import CaseMap from './CaseMap';

// ── 收藏 & 笔记 hooks ──
function useCaseFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('case_favorites') || '[]'); } catch { return []; }
  });
  const toggle = (id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem('case_favorites', JSON.stringify(next));
      return next;
    });
  };
  return { favorites, toggle };
}

function useCaseNotes() {
  const [notes, setNotes] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('case_notes') || '{}'); } catch { return {}; }
  });
  const save = (id: string, text: string) => {
    setNotes((prev) => {
      const next = { ...prev, [id]: text };
      localStorage.setItem('case_notes', JSON.stringify(next));
      return next;
    });
  };
  const remove = (id: string) => {
    setNotes((prev) => {
      const next = { ...prev };
      delete next[id];
      localStorage.setItem('case_notes', JSON.stringify(next));
      return next;
    });
  };
  return { notes, save, remove };
}

// ── 笔记弹窗 ──
interface NoteModalProps {
  caseId: string;
  caseName: string;
  initialNote: string;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

function NoteModal({ caseId, caseName, initialNote, onSave, onDelete, onClose }: NoteModalProps) {
  const [text, setText] = useState(initialNote);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSave = () => {
    if (text.trim()) {
      onSave(caseId, text.trim());
    } else {
      onDelete(caseId);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-[#2E1065] to-[#4C1D95]">
          <div className="flex items-center gap-2">
            <i className="ri-sticky-note-line text-white text-lg"></i>
            <div>
              <div className="text-white font-bold text-sm">案例笔记</div>
              <div className="text-white/60 text-xs line-clamp-1">{caseName}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors">
            <i className="ri-close-line text-white text-sm"></i>
          </button>
        </div>
        <div className="p-5">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 500))}
            placeholder="记录你对这个案例的思考、关键点或待跟进事项..."
            className="w-full h-40 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-[#7C3AED] transition-colors leading-relaxed"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gray-400">{text.length}/500</span>
            {initialNote && (
              <button onClick={() => { onDelete(caseId); onClose(); }} className="text-xs text-red-500 hover:text-red-700 cursor-pointer transition-colors">
                删除笔记
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-full text-sm text-gray-600 hover:border-gray-300 cursor-pointer transition-colors whitespace-nowrap">
            取消
          </button>
          <button onClick={handleSave} className="flex-1 py-2 bg-[#7C3AED] text-white rounded-full text-sm font-semibold hover:bg-[#6D28D9] cursor-pointer transition-colors whitespace-nowrap">
            保存笔记
          </button>
        </div>
      </div>
    </div>
  );
}

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  债券:       { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200', dot: 'bg-violet-500'  },
  房地产:     { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  大宗商品:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',  dot: 'bg-amber-500'   },
  股权:       { bg: 'bg-pink-50',    text: 'text-pink-700',    border: 'border-pink-200',   dot: 'bg-pink-500'    },
  碳信用:     { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200',   dot: 'bg-teal-500'    },
  供应链信贷: { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200', dot: 'bg-orange-500'  },
  其他:       { bg: 'bg-slate-50',   text: 'text-slate-600',   border: 'border-slate-200',  dot: 'bg-slate-400'   },
};

const STATUS_COLORS: Record<string, { dot: string; text: string }> = {
  运营中: { dot: 'bg-emerald-400', text: 'text-emerald-700' },
  试点:   { dot: 'bg-violet-400',  text: 'text-violet-700'  },
  已发行: { dot: 'bg-amber-400',   text: 'text-amber-700'   },
  规划中: { dot: 'bg-slate-400',   text: 'text-slate-600'   },
};

const STATS = [
  { label: '总案例数', value: '87', icon: 'ri-folder-3-line', color: '#7C3AED' },
  { label: '运营中项目', value: '63', icon: 'ri-checkbox-circle-line', color: '#34D399' },
  { label: '覆盖地区', value: '12+', icon: 'ri-map-pin-line', color: '#F59E0B' },
  { label: '资产赛道', value: '7类', icon: 'ri-grid-line', color: '#F97316' },
  { label: '含合约源码', value: '6个', icon: 'ri-code-s-slash-line', color: '#3B82F6' },
];

export default function CaseLibrary() {
  const [activeType, setActiveType] = useState<CaseType>('全部');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [noteModal, setNoteModal] = useState<{ id: string; name: string } | null>(null);
  const [showCompare, setShowCompare] = useState(false);
  const [compareInitial, setCompareInitial] = useState<typeof cases>([]);
  const [showMap, setShowMap] = useState(false);
  const perPage = 9;

  const { favorites, toggle: toggleFav } = useCaseFavorites();
  const { notes, save: saveNote, remove: removeNote } = useCaseNotes();

  const filtered = cases.filter((c) => {
    const matchType = activeType === '全部' || c.type === activeType;
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || c.desc.includes(q) || c.region.includes(q);
    const matchFav = !showFavOnly || favorites.includes(c.id);
    return matchType && matchSearch && matchFav;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleFilter = (t: CaseType) => { setActiveType(t); setPage(1); };

  return (
    <section id="cases" className="py-20 bg-white dark:bg-gray-900">
      {noteModal && (
        <NoteModal
          caseId={noteModal.id}
          caseName={noteModal.name}
          initialNote={notes[noteModal.id] || ''}
          onSave={saveNote}
          onDelete={removeNote}
          onClose={() => setNoteModal(null)}
        />
      )}
      {showCompare && (
        <CaseCompare
          initialCases={compareInitial}
          onClose={() => setShowCompare(false)}
        />
      )}
      {showMap && <CaseMap onClose={() => setShowMap(false)} />}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-full mb-4">
            <i className="ri-folder-chart-line text-[#7C3AED] text-sm"></i>
            <span className="text-[#7C3AED] text-sm font-semibold">04 · 案例深度库</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">
            全球 <span className="text-[#7C3AED]">87</span> 个 RWA 案例库
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base">
            覆盖美国、欧盟、香港、新加坡等主要市场，涵盖7大资产赛道，含智能合约源码、法律架构与合规路径完整解析，16维度深度拆解。
          </p>
          {/* 功能入口 */}
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <button
              onClick={() => { setCompareInitial([]); setShowCompare(true); }}
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#7C3AED]/10 border border-[#7C3AED]/25 text-[#7C3AED] rounded-full text-sm font-semibold hover:bg-[#7C3AED]/20 cursor-pointer transition-all whitespace-nowrap"
            >
              <i className="ri-scales-3-line text-sm"></i>
              案例横向对比
              <span className="text-xs opacity-60">最多3个并排</span>
            </button>
            <button
              onClick={() => setShowMap(true)}
              className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40 text-emerald-700 dark:text-emerald-400 rounded-full text-sm font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-900/30 cursor-pointer transition-all whitespace-nowrap"
            >
              <i className="ri-map-2-line text-sm"></i>
              全球案例地图
              <span className="text-xs opacity-60">地区分布</span>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#F8F7FF] dark:bg-gray-800 rounded-xl p-4 text-center border border-[#7C3AED]/8 dark:border-gray-700">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2" style={{ background: `${s.color}18` }}>
                <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
              </div>
              <div className="font-black text-gray-800 dark:text-gray-100 text-xl">{s.value}</div>
              <div className="text-gray-500 dark:text-gray-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Favorites Bar */}
        {favorites.length > 0 && (
          <div className="mb-5 bg-[#FFF8E6] dark:bg-amber-900/20 border border-[#F59E0B]/25 dark:border-amber-700/30 rounded-xl px-5 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 shrink-0">
                <i className="ri-star-fill text-[#F59E0B] text-sm"></i>
                <span className="text-xs font-bold text-amber-800 dark:text-amber-300">已收藏 {favorites.length} 个案例</span>
              </div>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {favorites.slice(0, 6).map((fid) => {
                  const fc = cases.find((c) => c.id === fid);
                  if (!fc) return null;
                  return (
                    <span key={fid} className="text-xs px-2.5 py-1 bg-[#F59E0B]/15 text-amber-800 dark:text-amber-300 rounded-full font-medium flex items-center gap-1">
                      {fc.name.slice(0, 12)}{fc.name.length > 12 ? '...' : ''}
                      {notes[fid] && <i className="ri-sticky-note-fill text-[#7C3AED] text-xs"></i>}
                    </span>
                  );
                })}
                {favorites.length > 6 && <span className="text-xs text-amber-600 dark:text-amber-400">+{favorites.length - 6}个</span>}
              </div>
              <button
                onClick={() => { setShowFavOnly(!showFavOnly); setPage(1); }}
                className={`text-xs px-3 py-1 rounded-full font-semibold cursor-pointer transition-all whitespace-nowrap ${showFavOnly ? 'bg-[#F59E0B] text-white' : 'border border-[#F59E0B]/40 text-amber-700 dark:text-amber-400 hover:bg-[#F59E0B]/10'}`}
              >
                {showFavOnly ? '显示全部' : '只看收藏'}
              </button>
            </div>
          </div>
        )}

        {/* Notes Summary */}
        {Object.keys(notes).length > 0 && (
          <div className="mb-5 bg-[#F5F3FF] dark:bg-[#7C3AED]/10 border border-[#7C3AED]/15 dark:border-[#7C3AED]/25 rounded-xl px-5 py-3 flex items-center gap-3">
            <i className="ri-sticky-note-line text-[#7C3AED] text-sm shrink-0"></i>
            <span className="text-xs text-[#7C3AED] font-medium">你已为 {Object.keys(notes).length} 个案例添加了笔记</span>
            <div className="flex flex-wrap gap-1.5 flex-1">
              {Object.keys(notes).slice(0, 4).map((nid) => {
                const nc = cases.find((c) => c.id === nid);
                if (!nc) return null;
                return (
                  <button
                    key={nid}
                    onClick={() => setNoteModal({ id: nid, name: nc.name })}
                    className="text-xs px-2 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded-full hover:bg-[#7C3AED]/20 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    {nc.name.slice(0, 10)}...
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {CASE_TYPES.map((t) => (
              <button key={t} onClick={() => handleFilter(t)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeType === t
                    ? 'bg-[#7C3AED] text-white'
                    : 'bg-[#F8F7FF] dark:bg-gray-800 border border-[#7C3AED]/15 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                }`}>
                {t}{t === '全部' && <span className="ml-1 text-xs opacity-70">({cases.length})</span>}
              </button>
            ))}
          </div>
          <div className="relative">
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="搜索案例、地区..."
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 rounded-full px-4 py-2 pl-9 text-sm w-52 focus:outline-none focus:border-[#7C3AED] transition-colors" />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          </div>
        </div>

        {/* Case Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {paginated.map((c) => {
            const tc = TYPE_COLORS[c.type] || TYPE_COLORS['其他'];
            const sc = STATUS_COLORS[c.status] || STATUS_COLORS['运营中'];
            const hasContract = !!c.contractCode;
            const isFav = favorites.includes(c.id);
            const hasNote = !!notes[c.id];
            return (
              <div key={c.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-5 hover:border-[#7C3AED]/30 hover:-translate-y-0.5 transition-all group relative">
                {/* Action Buttons */}
                <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
                  <button
                    onClick={(e) => { e.preventDefault(); setNoteModal({ id: c.id, name: c.name }); }}
                    title={hasNote ? '查看/编辑笔记' : '添加笔记'}
                    className={`w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer ${hasNote ? 'bg-[#7C3AED]/15 text-[#7C3AED]' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-[#7C3AED]/10 hover:text-[#7C3AED]'}`}
                  >
                    <i className={`${hasNote ? 'ri-sticky-note-fill' : 'ri-sticky-note-line'} text-xs`}></i>
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); toggleFav(c.id); }}
                    title={isFav ? '取消收藏' : '收藏案例'}
                    className={`w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer ${isFav ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-[#F59E0B]/10 hover:text-[#F59E0B]'}`}
                  >
                    <i className={`${isFav ? 'ri-star-fill' : 'ri-star-line'} text-xs`}></i>
                  </button>
                </div>

                <Link to={`/cases/${c.id}`} className="block" onClick={(e) => { if ((e.target as HTMLElement).closest('button')) e.preventDefault(); }}>
                  <div className="flex items-start justify-between mb-2 pr-16">
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${tc.bg} ${tc.text} ${tc.border}`}>{c.type}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#7C3AED]/8 text-[#7C3AED]">{c.track}</span>
                      {hasContract && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-[#1D4ED8]/8 text-[#1D4ED8] flex items-center gap-1">
                          <i className="ri-code-s-slash-line text-xs"></i>源码
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                      <span className={`text-xs ${sc.text} font-medium`}>{c.status}</span>
                    </div>
                  </div>
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 text-sm mb-1.5 leading-snug group-hover:text-[#7C3AED] transition-colors line-clamp-1">{c.name}</h4>
                  <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed mb-2 line-clamp-2">{c.desc}</p>
                  {hasNote && (
                    <div className="mb-2 px-2.5 py-1.5 bg-[#7C3AED]/5 border border-[#7C3AED]/10 rounded-lg">
                      <p className="text-xs text-[#7C3AED] line-clamp-1 italic">&ldquo;{notes[c.id]}&rdquo;</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2.5 border-t border-gray-50 dark:border-gray-700">
                    <span className="font-bold text-[#7C3AED] text-xs flex items-center gap-1">
                      <i className="ri-bar-chart-2-line text-xs"></i>{c.tvl}
                    </span>
                    <div className="flex items-center gap-3 text-gray-400 dark:text-gray-500 text-xs">
                      <span className="flex items-center gap-1"><i className="ri-map-pin-line text-xs"></i>{c.region}</span>
                      <span className="flex items-center gap-1"><i className="ri-links-line text-xs"></i>{c.platform.split('/')[0]}</span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mb-6">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-[#7C3AED] hover:text-[#7C3AED] disabled:opacity-40 cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-base"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium cursor-pointer transition-all ${p === page ? 'bg-[#7C3AED] text-white' : 'border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#7C3AED]'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-[#7C3AED] hover:text-[#7C3AED] disabled:opacity-40 cursor-pointer transition-colors">
              <i className="ri-arrow-right-s-line text-base"></i>
            </button>
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <div className="text-sm text-gray-400 dark:text-gray-500">
            当前展示 <span className="text-[#7C3AED] font-semibold">{filtered.length}</span> / 87 个案例 · 数据更新：2026.03
          </div>
          <Link to="/cases"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#7C3AED] text-white rounded-full font-semibold text-sm hover:bg-[#6D28D9] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-folder-chart-line"></i>进入完整案例库（含智能合约源码）
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
